import {
  Box,
  Flex,
  Grid,
  HStack,
  Input,
  Stack,
  useDisclosure,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IoRefreshOutline } from 'react-icons/io5';
import { useMemo, useState } from 'react';
import { RiAddCircleLine } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import {
  DtType,
  ProjectStatus,
  ProposalType,
  useDeleteProjectTemplateMutation,
  useProjectTemplatesQuery,
  useTriggerProjectUpdateMutation,
  useRetryProjectMutation,
  ProjectTemplatesDocument,
} from '../../generated/graphql';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { SearchInput } from '../helivan/SearchInput';
import { HelivanLoading } from '../HelivanLoading';
import { RequestModal } from '../Requests/RequestModal';
import { ProjectCard } from './ProjectCard';
import { useStatsigClient } from '@statsig/react-bindings';
import { CreateNewProjectModal } from './CreateNewProjectModal';
import { MdOutlineFolder, MdOutlineFolderShared } from 'react-icons/md';
import ProjectFilter from './ProjectFilter';
import { ProjectSettingsModal } from './ProjectSettingsModal';
import { useAuth } from '../../contexts/AuthContext';

export const Projects = () => {
  // `authLoading` is true while Firebase restores the session on a hard refresh. The
  // /projects page is public-readable, so without waiting we'd render the signed-out
  // public view first and then flip to the signed-in view — a jarring reload. Hold the
  // spinner until auth resolves so we render the correct view once.
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { data, loading } = useProjectTemplatesQuery();
  const projectTemplates = data?.getMyProjectTemplates ?? [];
  const navigate = useNavigate();
  const requestProjectModal = useDisclosure();
  const deleteConfirmModal = useDisclosure();
  const toast = useToast();
  const [deleteProjectTemplate, { loading: deleteLoading }] = useDeleteProjectTemplateMutation({
    refetchQueries: [{ query: ProjectTemplatesDocument }],
    awaitRefetchQueries: true,
  });
  const [triggerProjectUpdate, { loading: updateLoading }] = useTriggerProjectUpdateMutation({
    refetchQueries: [{ query: ProjectTemplatesDocument }],
    awaitRefetchQueries: true,
  });
  const [retryProject] = useRetryProjectMutation({
    refetchQueries: [{ query: ProjectTemplatesDocument }],
    awaitRefetchQueries: true,
  });
  const [projectToDelete, setProjectToDelete] = useState<{ id: string; title: string } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Settings modal state
  const settingsModal = useDisclosure();
  const [projectForSettings, setProjectForSettings] = useState<{ id: string; name: string } | null>(null);

  // Update confirmation modal state
  const updateConfirmModal = useDisclosure();
  const [projectToUpdate, setProjectToUpdate] = useState<{ templateId: string; title: string } | null>(null);

  const openDeleteConfirm = (projectId: string, projectTitle: string) => {
    setProjectToDelete({ id: projectId, title: projectTitle });
    setDeleteConfirmText('');
    deleteConfirmModal.onOpen();
  };

  const openSettings = (templateId: string, projectName: string) => {
    setProjectForSettings({ id: templateId, name: projectName });
    settingsModal.onOpen();
  };

  const openUpdateConfirm = (templateId: string, projectTitle: string) => {
    setProjectToUpdate({ templateId, title: projectTitle });
    updateConfirmModal.onOpen();
  };

  const handleRetryProject = async (projectId: string) => {
    try {
      await retryProject({ variables: { projectId } });
      toast({
        title: 'Retry started',
        description: 'Project generation has been restarted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Retry failed',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdateProject = async () => {
    if (!projectToUpdate?.templateId) return;

    try {
      await triggerProjectUpdate({
        variables: { input: { projectTemplateId: projectToUpdate.templateId } },
      });
      toast({
        title: 'Update triggered',
        description: 'A new version is being generated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      updateConfirmModal.onClose();
      setProjectToUpdate(null);
    } catch (error) {
      toast({
        title: 'Failed to trigger update',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await deleteProjectTemplate({
        variables: { id: projectToDelete.id },
      });
      toast({
        title: 'Project deleted',
        description: 'All watches have been removed.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      deleteConfirmModal.onClose();
      setProjectToDelete(null);
      setDeleteConfirmText('');
    } catch (error) {
      toast({
        title: 'Failed to delete project',
        description: 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const [selectedTypes, setSelectedTypes] = useState<DtType[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [viewType, setViewType] = useState<'all' | 'myProjects'>('all');

  // Filter project templates
  const filteredProjects = useMemo(() => {
    if (projectTemplates.length === 0) return [];

    let filtered = projectTemplates;

    // Filter by type
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(
        (template) =>
          template.digitalTwinType &&
          selectedTypes.includes(template.digitalTwinType)
      );
    }

    // Filter by myProjects
    if (viewType === 'myProjects') {
      filtered = filtered.filter((template) => template.createdByYou);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (template) =>
          template.topic.toLowerCase().includes(lowerCaseSearch) ||
          template.topicShortened?.toLowerCase().includes(lowerCaseSearch) ||
          template.subtopicsShortened?.some((subtopic) =>
            subtopic.toLowerCase().includes(lowerCaseSearch)
          )
      );
    }

    return filtered;
  }, [projectTemplates, selectedTypes, searchTerm, viewType]);

  const { checkGate } = useStatsigClient();
  const canCreateProject = checkGate('create_project');
  const createNewModal = useDisclosure();

  return loading || authLoading ? (
    <HelivanLoading />
  ) : (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      <RequestModal
        isOpen={requestProjectModal.isOpen}
        onClose={requestProjectModal.onClose}
        prefill={ProposalType.Project}
      />
      <CreateNewProjectModal
        isOpen={createNewModal.isOpen}
        onClose={createNewModal.onClose}
        hasTokens={data?.user?.canCreateProject ?? false}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModal.isOpen} onClose={() => { deleteConfirmModal.onClose(); setDeleteConfirmText(''); }} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" mx={4} overflow="hidden">
          <Box bg="red.50" borderBottom="1px solid" borderColor="red.200" px={6} py={4}>
            <ModalHeader p={0} color="red.700" fontSize="lg" fontWeight="600">
              Danger Zone
            </ModalHeader>
          </Box>
          <ModalBody py={6} px={6}>
            <VStack spacing={4} align="stretch">
              <Text color="gray.700" fontSize="15px">
                Are you sure you want to delete this project?
              </Text>
              {projectToDelete && (
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text fontWeight="600" color="gray.800" fontSize="14px">
                    {projectToDelete.title}
                  </Text>
                </Box>
              )}
              <Box
                p={3}
                bg="red.50"
                borderRadius="8px"
                border="1px solid"
                borderColor="red.200"
              >
                <Text color="red.700" fontSize="13px">
                  This action cannot be undone. All project data, perspectives, and associated files will be permanently deleted.
                </Text>
              </Box>
              <Box>
                <Text color="gray.700" fontSize="13px" mb={2}>
                  To confirm, type the project name below:
                </Text>
                <Input
                  placeholder={projectToDelete?.title ?? ''}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  size="sm"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'red.500', boxShadow: '0 0 0 1px var(--chakra-colors-red-500)' }}
                />
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200" gap={3}>
            <Button
              variant="outline"
              onClick={() => { deleteConfirmModal.onClose(); setDeleteConfirmText(''); }}
              isDisabled={deleteLoading}
              size="md"
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDeleteProject}
              isLoading={deleteLoading}
              loadingText="Deleting..."
              size="md"
              isDisabled={deleteConfirmText !== projectToDelete?.title}
            >
              Delete Project
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Update Confirmation Modal */}
      <Modal isOpen={updateConfirmModal.isOpen} onClose={() => { updateConfirmModal.onClose(); setProjectToUpdate(null); }} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="16px" mx={4} overflow="hidden">
          <Box bg={HelivanColor.background.secondary} borderBottom="1px solid" borderColor={HelivanColor.border.primary} px={6} py={4}>
            <ModalHeader p={0} color={HelivanColor.text.blueDark} fontSize="lg" fontWeight="600">
              <HStack spacing={2}>
                <IoRefreshOutline size={20} />
                <Text>Update Project</Text>
              </HStack>
            </ModalHeader>
          </Box>
          <ModalBody py={6} px={6}>
            <VStack spacing={4} align="stretch">
              <Text color="gray.700" fontSize="15px">
                This will generate a new version of the project with the latest data.
              </Text>
              {projectToUpdate && (
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Text fontWeight="600" color="gray.800" fontSize="14px">
                    {projectToUpdate.title}
                  </Text>
                </Box>
              )}
              <Box
                p={3}
                bg="blue.50"
                borderRadius="8px"
                border="1px solid"
                borderColor="blue.200"
              >
                <Text color="blue.700" fontSize="13px">
                  The new version will appear in your timeline once generation is complete.
                </Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderTop="1px solid" borderColor="gray.200" gap={3}>
            <Button
              variant="outline"
              onClick={() => { updateConfirmModal.onClose(); setProjectToUpdate(null); }}
              isDisabled={updateLoading}
              size="md"
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateProject}
              isLoading={updateLoading}
              loadingText="Triggering..."
              size="md"
              leftIcon={<IoRefreshOutline />}
            >
              Update Now
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Project Settings Modal */}
      {projectForSettings && (
        <ProjectSettingsModal
          isOpen={settingsModal.isOpen}
          onClose={() => { settingsModal.onClose(); setProjectForSettings(null); }}
          projectTemplateId={projectForSettings.id}
          projectName={projectForSettings.name}
        />
      )}

      {/* Header - full width */}
      <Flex
        width="full"
        direction={{ base: 'column', lg: 'row' }}
        alignItems={{ base: 'stretch', lg: 'center' }}
        py={{ base: '8px', md: '12px' }}
        px={{ base: '12px', sm: '16px', md: '24px' }}
        minHeight="64px"
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        justifyContent="space-between"
        gap={{ base: 3, lg: 4 }}
        flexShrink={0}
      >
        {/* Row 1 on mobile: Title + Create button */}
        <Flex
          width="full"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          {/* Left: Title and view toggles */}
          <HStack spacing={{ base: 2, md: 6 }} flex="0 0 auto">
            <HelivanText variant="sectionHeader">Watches</HelivanText>
            <HStack spacing={1} display={{ base: 'none', sm: 'flex' }}>
              <Box
                onClick={() => setViewType('all')}
                px={{ base: '6px', md: '8px' }}
                py="4px"
                bg={viewType === 'all' ? HelivanColor.background.secondary : 'white'}
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                borderRadius="6px"
                _hover={{ bg: HelivanColor.background.secondary }}
                cursor="pointer"
              >
                <HStack spacing={{ base: 1, md: 2 }}>
                  <MdOutlineFolder size={14} color={HelivanColor.text.blueMid} />
                  <HelivanText variant="smallBody">All</HelivanText>
                </HStack>
              </Box>
              <Box
                onClick={() => setViewType('myProjects')}
                px={{ base: '6px', md: '8px' }}
                py="4px"
                bg={viewType === 'myProjects' ? HelivanColor.background.secondary : 'white'}
                border="1px solid"
                borderColor={HelivanColor.border.primary}
                borderRadius="6px"
                _hover={{ bg: HelivanColor.background.secondary }}
                cursor="pointer"
              >
                <HStack spacing={{ base: 1, md: 2 }}>
                  <MdOutlineFolderShared size={14} color={HelivanColor.text.blueMid} />
                  <HelivanText variant="smallBody" display={{ base: 'none', md: 'block' }}>Created by You</HelivanText>
                  <HelivanText variant="smallBody" display={{ base: 'block', md: 'none' }}>Mine</HelivanText>
                </HStack>
              </Box>
            </HStack>
            {/* Search: inline immediately right of the pills on desktop */}
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search…"
              containerProps={{ display: { base: 'none', sm: 'block' } }}
            />
          </HStack>

          {/* Right: Create button (always visible) */}
          <HStack spacing={{ base: 1, md: 3 }} flex="0 0 auto">
            <Text
              as="button"
              fontSize="13px"
              fontWeight={500}
              color={HelivanColor.text.helivanBlue}
              whiteSpace="nowrap"
              display={{ base: 'none', md: 'block' }}
              _hover={{ textDecoration: 'underline' }}
              onClick={() => navigate('/projects/demo')}
            >
              See an example
            </Text>
            <ProjectFilter
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
              type="project"
            />
            {!isAuthenticated ? (
              <HelivanButton data-tour="new-watch-btn" onClick={() => navigate('/login')} variant="green" size="sm">
                <HStack spacing={1}>
                  <RiAddCircleLine color="white" size={14} />
                  <HelivanText colorOverride="white" variant="smallBody" display={{ base: 'none', sm: 'block' }}>
                    New watch
                  </HelivanText>
                </HStack>
              </HelivanButton>
            ) : canCreateProject ? (
              <HelivanButton data-tour="new-watch-btn" onClick={createNewModal.onOpen} variant="green" size="sm">
                <HStack spacing={1}>
                  <RiAddCircleLine color="white" size={14} />
                  <HelivanText colorOverride="white" variant="smallBody" display={{ base: 'none', sm: 'block' }}>
                    New watch
                  </HelivanText>
                </HStack>
              </HelivanButton>
            ) : (
              <HelivanButton data-tour="new-watch-btn" onClick={requestProjectModal.onOpen} variant="green" size="sm">
                <HStack spacing={1}>
                  <RiAddCircleLine color="white" size={14} />
                  <HelivanText colorOverride="white" variant="smallBody" display={{ base: 'none', sm: 'block' }}>
                    Request watch
                  </HelivanText>
                </HStack>
              </HelivanButton>
            )}
          </HStack>
        </Flex>

        {/* Row 2 on mobile only: full-width search bar */}
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search…"
          containerProps={{ display: { base: 'block', sm: 'none' }, width: 'full' }}
        />
      </Flex>

      {/* Content area - separate from header */}
      <Flex
        flex="1"
        overflowY="auto"
        bg={HelivanColor.background.secondary}
        p={{ base: 3, sm: 4, md: 6 }}
        justifyContent="center"
      >
        {filteredProjects.length > 0 ? (
          <Grid
            templateColumns={[
              'repeat(1, 1fr)',
              'repeat(2, 1fr)',
              'repeat(2, 1fr)',
              'repeat(3, 1fr)',
              'repeat(4, 1fr)',
              'repeat(5, 1fr)',
            ]}
            gap={{ base: 3, md: 5 }}
            width="full"
            maxWidth="1400px"
            height="fit-content"
          >
            {filteredProjects.map((template, index) => (
              <ProjectCard
                tourAnchor={index === 0}
                onClick={
                  template.hasCompletedProject && template.latestCompletedProjectId
                    ? () => navigate(`/projects/${template.latestCompletedProjectId}`)
                    : undefined
                }
                dateCreated={template.latestProjectDate ?? ''}
                id={template.id}
                industry={template.digitalTwinType}
                watchType={template.type}
                agentName={template.agentName}
                subtopics={template.subtopicsShortened ?? []}
                title={template.topicShortened ?? template.topic}
                key={template.id}
                hasCompleted={template.hasCompletedProject ?? false}
                hasFailed={template.latestProjectStatus === ProjectStatus.Failed}
                errorMessage={template.latestProjectErrorMessage}
                explorableCount={template.projectCount ?? 0}
                agentCount={template.agentCount ?? 0}
                onRetry={
                  template.latestProjectStatus === ProjectStatus.Failed && template.latestProjectId
                    ? () => handleRetryProject(template.latestProjectId!)
                    : undefined
                }
                onDelete={() => openDeleteConfirm(
                  template.id,
                  template.topicShortened ?? template.topic
                )}
                onSettings={() => openSettings(
                  template.id,
                  template.topicShortened ?? template.topic
                )}
                onUpdate={() => openUpdateConfirm(
                  template.id,
                  template.topicShortened ?? template.topic
                )}
                nextScheduledUpdate={template.nextScheduledUpdate}
                updateFrequency={template.updateFrequency}
                autoUpdateEnabled={template.autoUpdateEnabled}
              />
            ))}
          </Grid>
        ) : (
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="center"
            width="full"
            py={16}
            gap={2}
          >
            {isAuthenticated && canCreateProject && !searchTerm.trim() && selectedTypes.length === 0 ? (
              <Flex
                as="button"
                w="56px"
                h="56px"
                borderRadius="16px"
                bg="#00477D"
                align="center"
                justify="center"
                mb={2}
                cursor="pointer"
                transition="all 0.15s"
                boxShadow="0 2px 8px rgba(0,71,125,0.3)"
                _hover={{
                  bg: '#003d6b',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
                }}
                _active={{ transform: 'scale(0.96)' }}
                onClick={createNewModal.onOpen}
              >
                <RiAddCircleLine size={26} color="white" />
              </Flex>
            ) : (
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bg="rgba(0,71,125,0.06)"
                align="center"
                justify="center"
                mb={2}
              >
                <MdOutlineFolder size={24} color="#00477D" />
              </Flex>
            )}
            <Text fontSize="16px" fontWeight="600" color="#1a1a2e">
              No watches yet
            </Text>
            <Text fontSize="13px" color="#64748b">
              {searchTerm.trim() || selectedTypes.length > 0
                ? 'Try adjusting your search or filters'
                : 'Set up your first watch to start monitoring an agent.'}
            </Text>
            {!searchTerm.trim() && selectedTypes.length === 0 && (
              <Text
                as="button"
                mt={2}
                fontSize="13px"
                fontWeight={600}
                color={HelivanColor.text.helivanBlue}
                _hover={{ textDecoration: 'underline' }}
                onClick={() => navigate('/projects/demo')}
              >
                New to AgentWatch? See an example →
              </Text>
            )}
          </Flex>
        )}
      </Flex>
    </Stack>
  );
};
